// src/components/Layout/BodegaList.js
import React, { useEffect, useState } from 'react';
import { getBodegas, deleteBodega, getStockBodega } from '../../services/api';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TableContainer,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const BodegaList = () => {
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [stockError, setStockError] = useState('');
  const [stockLoading, setStockLoading] = useState(false);

  useEffect(() => {
    const fetchBodegas = async () => {
      try {
        const data = await getBodegas();
        if (data.success) {
          setBodegas(data.data);
        } else {
          setError('Error al obtener bodegas');
        }
      } catch (err) {
        setError(err.error || 'Error al obtener bodegas');
      } finally {
        setLoading(false);
      }
    };

    fetchBodegas();
  }, []);

  const handleEdit = (id) => {
    navigate(`/bodegas/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = await deleteBodega(selectedId);
      if (data.success) {
        setBodegas(bodegas.filter((bodega) => bodega.id !== selectedId));
      } else {
        setError('Error al eliminar bodega');
      }
    } catch (err) {
      setError(err.error || 'Error al eliminar bodega');
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleViewStock = async (idBodega) => {
    setStockLoading(true);
    setStockError('');
    setStockData([]);
    try {
      const data = await getStockBodega(idBodega);
      if (data.success) {
        setStockData(data.stock);
      } else {
        setStockError('Error al obtener stock de la bodega');
      }
    } catch (err) {
      setStockError(err.error || 'Error al obtener stock de la bodega');
    } finally {
      setStockLoading(false);
      setOpenStockDialog(true);
    }
  };

  const [openStockDialog, setOpenStockDialog] = useState(false);

  const handleCloseStockDialog = () => {
    setOpenStockDialog(false);
    setStockData([]);
    setStockError('');
  };

  if (loading) return <CircularProgress />;

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
        <Typography variant="h4">Bodegas</Typography>
        <Button variant="contained" color="primary" onClick={() => navigate('/bodegas/new')}>
          Crear Bodega
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bodegas.map((bodega) => (
              <TableRow key={bodega.id}>
                <TableCell>{bodega.id}</TableCell>
                <TableCell>{bodega.nombre}</TableCell>
                <TableCell>{bodega.direccion}</TableCell>
                <TableCell>{bodega.estado}</TableCell>
                <TableCell>
                  <Button variant="outlined" color="primary" onClick={() => handleEdit(bodega.id)}>
                    Editar
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleDeleteClick(bodega.id)}
                    style={{ marginLeft: '8px' }}
                  >
                    Eliminar
                  </Button>
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={() => handleViewStock(bodega.id)}
                    style={{ marginLeft: '8px' }}
                  >
                    Ver Stock
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Confirmación para Eliminar */}
      <Dialog
        open={openDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar esta bodega? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para Mostrar Stock */}
      <Dialog
        open={openStockDialog}
        onClose={handleCloseStockDialog}
        fullWidth
        maxWidth="md"
        aria-labelledby="stock-dialog-title"
      >
        <DialogTitle id="stock-dialog-title">Stock de la Bodega</DialogTitle>
        <DialogContent>
          {stockLoading && <CircularProgress />}
          {stockError && <Alert severity="error">{stockError}</Alert>}
          {!stockLoading && !stockError && stockData.length === 0 && (
            <Typography>No hay stock registrado para esta bodega.</Typography>
          )}
          {!stockLoading && !stockError && stockData.length > 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID Producto</TableCell>
                    <TableCell>Nombre Producto</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Última Actualización</TableCell>
                    <TableCell>Usuario Actualización</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.idProducto}</TableCell>
                      <TableCell>{item.nombreProducto}</TableCell>
                      <TableCell>{item.stock}</TableCell>
                      <TableCell>
                        {item.ultimaActualizacion
                          ? new Date(item.ultimaActualizacion).toLocaleString()
                          : 'Sin actualización'}
                      </TableCell>
                      <TableCell>{item.usuarioActualizacion || 'Desconocido'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStockDialog} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BodegaList;
